import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketService, clientService, appService, slaService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Send } from 'lucide-react';
import { format } from 'date-fns';
import './NewTicket.css';

const CATEGORIES = ['Bug', 'Feature Request', 'Performance', 'Access/Auth', 'Data Issue', 'Integration', 'Other'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

export default function NewTicket() {
  const { user, isSupport } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [apps, setApps] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [slaPreview, setSlaPreview] = useState(null);

  const [form, setForm] = useState({
    title: '', description: '', category: 'Bug', priority: 'Medium',
    clientId: user?.client || '', appId: '', reportedByEmail: user?.email || '',
  });

  useEffect(() => {
    if (isSupport) clientService.getAll().then(r => setClients(r.data.clients));
    appService.getAll().then(r => setApps(r.data.apps));
  }, [isSupport]);

  useEffect(() => {
    if (!form.clientId || !form.appId) {
      setSlaPreview(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await slaService.preview({
          clientId: form.clientId,
          appId: form.appId,
          priority: form.priority,
          category: form.category,
        });
        setSlaPreview(res.data.preview);
      } catch {
        setSlaPreview(null);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [form.clientId, form.appId, form.priority, form.category]);

  const field = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.appId) return setError('Please fill all required fields.');
    setError(''); setSubmitting(true);
    try {
      const res = await ticketService.create(form);
      navigate(`/tickets/${res.data.ticket._id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create ticket');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="new-ticket">
      <button className="btn btn-ghost back-btn" onClick={() => navigate('/tickets')}>
        <ArrowLeft size={14} /> Back
      </button>

      <div className="page-header">
        <div>
          <h1 className="page-title">New Ticket</h1>
          <p className="page-subtitle">Report an issue or request</p>
        </div>
      </div>

      <div className="new-ticket__layout">
        <form onSubmit={handleSubmit} className="card new-ticket__form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-section">
            <label className="form-label">Title <span className="required">*</span></label>
            <input placeholder="Brief description of the issue" value={form.title} onChange={e => field('title', e.target.value)} />
          </div>

          <div className="form-section">
            <label className="form-label">Description <span className="required">*</span></label>
            <textarea rows={6} placeholder="Describe the issue in detail. Include steps to reproduce, expected vs actual behaviour..." value={form.description} onChange={e => field('description', e.target.value)} />
          </div>

          <div className="form-row">
            <div className="form-section">
              <label className="form-label">Category</label>
              <select value={form.category} onChange={e => field('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-section">
              <label className="form-label">Priority</label>
              <select value={form.priority} onChange={e => field('priority', e.target.value)}>
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            {isSupport && (
              <div className="form-section">
                <label className="form-label">Client <span className="required">*</span></label>
                <select value={form.clientId} onChange={e => field('clientId', e.target.value)}>
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="form-section">
              <label className="form-label">Application <span className="required">*</span></label>
              <select value={form.appId} onChange={e => field('appId', e.target.value)}>
                <option value="">Select app...</option>
                {apps.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">Your Email</label>
            <input type="email" value={form.reportedByEmail} onChange={e => field('reportedByEmail', e.target.value)} placeholder="contact@yourcompany.com" />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/tickets')}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              <Send size={13} /> {submitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </div>
        </form>

        <div className="new-ticket__tips card">
          <h4>Tips for faster resolution</h4>
          <ul>
            <li>Include exact error messages or codes</li>
            <li>Mention steps to reproduce the issue</li>
            <li>Note if the issue is intermittent or consistent</li>
            <li>Attach screenshots if possible</li>
            <li>Set <strong>Critical</strong> only for production-down issues</li>
          </ul>

          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <h4 style={{ marginBottom: 8 }}>SLA Preview</h4>
            {!slaPreview && <p className="page-subtitle">Select client + application to preview SLA</p>}
            {slaPreview && (
              <div style={{ display: 'grid', gap: 6 }}>
                <div><strong>Policy:</strong> {slaPreview.policyName}</div>
                <div><strong>Response due:</strong> {format(new Date(slaPreview.responseDueAt), 'MMM d, yyyy h:mm a')}</div>
                <div><strong>Resolution due:</strong> {format(new Date(slaPreview.resolutionDueAt), 'MMM d, yyyy h:mm a')}</div>
                <div><strong>Warning threshold:</strong> {slaPreview.warningThresholdPct}%</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
